import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import socket from '../socket';
import {
  getAnnouncementById, reactToAnnouncement, unreactToAnnouncement,
  commentOnAnnouncement, replyToComment, updateAnnouncement, pinAnnouncement,
  deleteAnnouncement, deleteAnnouncementComment, updateAnnouncementComment,
  reactToComment, unreactToComment, deleteAttachment, uploadAttachments,
  getPoll, votePoll, retractPollVote,
} from '../api/apiClient';
import RichTextInput from './RichTextInput';
import StaffProfilePopup from './StaffProfilePopup';
import './AnnouncementCard.css';

const parseMentions = (raw) => {
  try { return JSON.parse(raw || '[]'); } catch { return []; }
};

const HTML_TAG_RE = /<[a-z][\s\S]*?>/i;
const isHTML = str => HTML_TAG_RE.test(str || '');

const ALLOWED_TAGS = new Set(['B','STRONG','I','EM','U','S','UL','OL','LI','A','BR','P','DIV','SPAN','H1','H2','H3','FONT']);
const sanitizeHTML = (html) => {
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  const clean = el => {
    for (const child of [...el.childNodes]) {
      if (child.nodeType === Node.TEXT_NODE) continue;
      if (child.nodeType !== Node.ELEMENT_NODE) { child.remove(); continue; }
      if (!ALLOWED_TAGS.has(child.tagName)) { child.replaceWith(document.createTextNode(child.textContent)); continue; }
      for (const attr of [...child.attributes]) {
        const ok = (child.tagName === 'A' && ['href','target','rel'].includes(attr.name)) ||
                   (child.tagName === 'FONT' && ['size','color'].includes(attr.name)) ||
                   (child.tagName === 'SPAN' && ['class','data-id','data-name','contenteditable'].includes(attr.name)) ||
                   (child.tagName === 'IMG' && attr.name === 'src');
        if (!ok) child.removeAttribute(attr.name);
      }
      if (child.tagName === 'A') {
        const href = child.getAttribute('href') || '';
        if (!href.startsWith('http') && !href.startsWith('mailto:') && !href.startsWith('/')) child.removeAttribute('href');
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }
      clean(child);
    }
  };
  clean(wrap);
  return wrap.innerHTML;
};

const renderContent = (text, mentionsRaw, onMentionClick) => {
  if (!text) return null;
  const mentions = parseMentions(mentionsRaw);
  if (!mentions.length) return text;
  const escaped = mentions.map(m => m.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(@(?:${escaped.join('|')}))`, 'g');
  return text.split(pattern).map((part, i) => {
    const m = mentions.find(m => `@${m.name}` === part);
    if (m) return (
      <span key={i} className="mention-pill mention-pill--display" onClick={() => onMentionClick(m.id)}>
        {part}
      </span>
    );
    return part;
  });
};

const REACTION_EMOJIS = ['👍', '👎', '❤️', '👏', '🎉', '🔥', '👀', '😂', '😢', '😮'];

const MAX_TOOLTIP_NAMES = 6;

const ReactionPill = ({ r, isMine, isPopping, onClick }) => {
  const [tooltip, setTooltip] = useState(false);
  const holdTimer = useRef(null);
  const wasHold = useRef(false);

  const startHold = () => {
    wasHold.current = false;
    holdTimer.current = setTimeout(() => {
      wasHold.current = true;
      setTooltip(true);
    }, 400);
  };

  const endHold = () => {
    clearTimeout(holdTimer.current);
    // tooltip stays visible until mouse leaves
  };

  const handleClick = () => {
    if (wasHold.current) {
      wasHold.current = false;
      return; // hold to peek — don't toggle reaction
    }
    onClick();
  };

  const names = r.reactor_names || [];
  const shown = names.slice(0, MAX_TOOLTIP_NAMES);
  const overflow = names.length - shown.length;

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseLeave={() => { setTooltip(false); wasHold.current = false; }}
    >
      {tooltip && names.length > 0 && (
        <div className="reaction-tooltip">
          <span className="reaction-tooltip-emoji">{r.emoji}</span>
          <span className="reaction-tooltip-text">
            {shown.map(n => n.split(' ')[0]).join(', ')}
            {overflow > 0 ? ` +${overflow} more` : ''}
          </span>
        </div>
      )}
      <button
        className={`reaction-pill${isMine ? ' reaction-pill--mine' : ''}`}
        onClick={handleClick}
        onMouseDown={startHold}
        onMouseUp={endHold}
        onTouchStart={startHold}
        onTouchEnd={() => {
          clearTimeout(holdTimer.current);
          if (wasHold.current) setTimeout(() => { setTooltip(false); wasHold.current = false; }, 1500);
        }}
        title={isMine ? 'Remove reaction' : 'React'}
      >
        <span className={isPopping ? 'reaction-emoji reaction-pop' : 'reaction-emoji'}>{r.emoji}</span>
        <span className="reaction-count">{r.count}</span>
      </button>
    </div>
  );
};

const formatDate = (raw) => {
  if (!raw) return '';
  const utc = raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z';
  return new Date(utc).toLocaleString();
};

// Date only, no time — used for the announcement post date.
const formatPostDate = (raw) => {
  if (!raw) return '';
  const utc = raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z';
  return new Date(utc).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

// created_at → 'YYYY-MM-DD' for a <input type="date"> (local calendar date).
const toDateInputValue = (raw) => {
  if (!raw) return '';
  const utc = raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z';
  const d = new Date(utc);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const buildTree = (flat) => {
  const map = {};
  flat.forEach(c => { map[c.id] = { ...c, children: [] }; });
  const roots = [];
  flat.forEach(c => {
    if (c.parent_id && map[c.parent_id]) map[c.parent_id].children.push(map[c.id]);
    else roots.push(map[c.id]);
  });
  return roots;
};

const PollBlock = ({ poll, currentUser, onVoted }) => {
  const voted = poll.my_vote_option_id !== null;

  const handleVote = async (optionId) => {
    if (!currentUser?.id) return;
    try {
      if (poll.my_vote_option_id === optionId) {
        await retractPollVote(poll.id, currentUser.id);
      } else {
        await votePoll(poll.id, { staff_id: currentUser.id, option_id: optionId });
      }
      onVoted();
    } catch (err) {
      console.error('Vote failed:', err);
    }
  };

  return (
    <div className="poll-block">
      <p className="poll-question">{poll.question}</p>
      <div className="poll-options">
        {poll.options.map(opt => {
          const pct = poll.total_votes > 0 ? Math.round((opt.vote_count / poll.total_votes) * 100) : 0;
          const isMyVote = poll.my_vote_option_id === opt.id;
          return (
            <button
              key={opt.id}
              className={`poll-option${isMyVote ? ' poll-option--mine' : ''}`}
              onClick={() => handleVote(opt.id)}
            >
              <div className="poll-option-top">
                <span className="poll-radio">
                  {isMyVote
                    ? <svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="8" fill="none" stroke="#272e40" strokeWidth="1.8"/><circle cx="9" cy="9" r="4.5" fill="#272e40"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="8" fill="none" stroke="#c0c4cc" strokeWidth="1.8"/></svg>
                  }
                </span>
                <span className="poll-option-label">{opt.text}</span>
                {voted && <span className="poll-pct">{pct}%</span>}
              </div>
              {voted && (
                <div className="poll-progress">
                  <div className={`poll-progress-fill${isMyVote ? ' poll-progress-fill--mine' : ''}`} style={{ width: `${pct}%` }} />
                </div>
              )}
            </button>
          );
        })}
      </div>
      <p className="poll-total">{poll.total_votes} {poll.total_votes === 1 ? 'vote' : 'votes'}</p>
    </div>
  );
};


const CommentNode = ({ comment, announcementId, currentUser, isAdmin, onDelete, onReply, onProfileOpen, depth = 0, forceExpandIds, focusCommentId }) => {
  const [collapsed,      setCollapsed]      = useState(false);
  const [showReplyForm,  setShowReplyForm]  = useState(false);
  const [replyData,      setReplyData]      = useState({ text: '', mentions: [] });
  const [submitting,     setSubmitting]     = useState(false);
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [isEditing,      setIsEditing]      = useState(false);
  const [editText,       setEditText]       = useState(comment.comment);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [threadExpanded,    setThreadExpanded]    = useState(false);
  const [commentPickerOpen, setCommentPickerOpen] = useState(false);
  const [poppingEmoji,      setPoppingEmoji]      = useState(null);
  const [commentPoll,       setCommentPoll]       = useState(undefined); // undefined=loading, null=none

  const fetchCommentPoll = useCallback(async () => {
    try {
      const res = await getPoll('comment', comment.id, currentUser?.id);
      setCommentPoll(res.data);
    } catch { setCommentPoll(null); }
  }, [comment.id, currentUser?.id]);

  useEffect(() => { fetchCommentPoll(); }, [fetchCommentPoll]);
  const menuRef          = useRef(null);
  const commentPickerRef = useRef(null);
  const replyInputRef    = useRef(null);
  const hasChildren = comment.children?.length > 0;
  const canManage = isAdmin || (currentUser && currentUser.id === comment.author_id);
  const effectiveThreadExpanded = threadExpanded || Boolean(forceExpandIds?.has(comment.id));
  const visibleChildren = effectiveThreadExpanded ? comment.children : (comment.children || []).slice(0, 1);
  const hiddenChildCount = (comment.children?.length || 0) - 1;
  const myCommentReaction = currentUser
    ? ((comment.reactions || []).find(r => r.reactor_ids.includes(currentUser.id)) || null)
    : null;

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  useEffect(() => {
    if (!commentPickerOpen) return;
    const handler = (e) => {
      if (commentPickerRef.current && !commentPickerRef.current.contains(e.target)) setCommentPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [commentPickerOpen]);

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyData.text.trim()) return;
    setSubmitting(true);
    try {
      await replyToComment(announcementId, comment.id, {
        author_id: currentUser.id,
        comment: replyData.text.trim(),
        mentions: replyData.mentions,
      });
      setReplyData({ text: '', mentions: [] });
      replyInputRef.current?.clear();
      setShowReplyForm(false);
      onReply();
    } catch (err) {
      console.error('Reply failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editText.trim()) return;
    setEditSubmitting(true);
    try {
      await updateAnnouncementComment(announcementId, comment.id, {
        staff_id: currentUser.id,
        comment: editText.trim(),
      });
      setIsEditing(false);
      setMenuOpen(false);
      onReply();
    } catch (err) {
      console.error('Edit failed:', err);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCommentReact = async (emoji) => {
    if (!currentUser?.id) return;
    try {
      if (myCommentReaction?.emoji === emoji) {
        await unreactToComment(announcementId, comment.id, currentUser.id);
      } else {
        await reactToComment(announcementId, comment.id, currentUser.id, emoji);
        setPoppingEmoji(emoji);
        setTimeout(() => setPoppingEmoji(null), 400);
      }
      onReply();
    } catch (err) {
      console.error('Comment react failed:', err);
    }
  };

  return (
    <div className="comment-node">
      <div
        className={`comment-item${comment.id === focusCommentId ? ' comment-item--focus' : ''}`}
        id={`comment-${comment.id}`}
      >
        <div className="comment-meta">
          <span className="comment-author author-link" onClick={() => onProfileOpen?.(comment.author_id)}>{comment.author_name}</span>
          <span className="comment-timestamp">{formatDate(comment.created_at)}</span>
          {canManage && (
            <div className="comment-action-menu-container" ref={menuRef}>
              <button className="comment-action-menu-btn" onClick={() => setMenuOpen(v => !v)}>⋯</button>
              {menuOpen && (
                <div className="comment-action-dropdown">
                  <button type="button" onClick={() => { setIsEditing(true); setMenuOpen(false); }}>Edit</button>
                  <button type="button" onClick={() => { onDelete(comment.id); setMenuOpen(false); }}>Delete</button>
                </div>
              )}
            </div>
          )}
        </div>
        {isEditing ? (
          <form className="comment-edit-form" onSubmit={handleEditSubmit}>
            <textarea
              className="comment-edit-textarea"
              value={editText}
              onChange={e => setEditText(e.target.value)}
              rows={2}
              autoFocus
            />
            <div className="reply-form-btns">
              <button type="submit" disabled={editSubmitting} className="reply-submit-btn">
                {editSubmitting ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="reply-cancel-btn" onClick={() => { setIsEditing(false); setEditText(comment.comment); }}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="comment-bubble">
            <p>{renderContent(comment.comment, comment.mentions, (id) => onProfileOpen?.(id))}</p>
          </div>
        )}
        {commentPoll && (
          <PollBlock poll={commentPoll} currentUser={currentUser} onVoted={fetchCommentPoll} />
        )}
        {!isEditing && (
          <div className="reactions-row comment-reactions-row" ref={commentPickerRef}>
            {(comment.reactions || []).map(r => {
              const isMine = currentUser && r.reactor_ids.includes(currentUser.id);
              return (
                <ReactionPill
                  key={r.emoji}
                  r={r}
                  isMine={isMine}
                  isPopping={poppingEmoji === r.emoji}
                  onClick={() => handleCommentReact(r.emoji)}
                />
              );
            })}
            {currentUser?.id && (
              <button className="add-reaction-btn" onClick={() => setCommentPickerOpen(v => !v)} title="Add reaction">
                <span>😊</span><span className="add-reaction-plus">+</span>
              </button>
            )}
            {commentPickerOpen && (
              <div className="emoji-picker-wrapper">
                <div className="emoji-picker-quick">
                  {REACTION_EMOJIS.map(e => (
                    <button key={e} className="emoji-picker-quick-btn" onClick={() => { handleCommentReact(e); setCommentPickerOpen(false); }}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {currentUser?.id && !isEditing && (
        <button className="reply-btn" onClick={() => setShowReplyForm(v => !v)}>
          ↩ Reply
        </button>
      )}

      {showReplyForm && (
        <form className="reply-form" onSubmit={handleReplySubmit}>
          <RichTextInput
            ref={replyInputRef}
            placeholder={`Reply to ${comment.author_name}…`}
            className="rich-input--comment"
            onChangeValue={(text, mentions) => setReplyData({ text, mentions })}
          />
          <div className="reply-form-btns">
            <button type="submit" disabled={submitting} className="reply-submit-btn">
              {submitting ? 'Posting…' : 'Post reply'}
            </button>
            <button type="button" className="reply-cancel-btn" onClick={() => { setShowReplyForm(false); setReplyData({ text: '', mentions: [] }); replyInputRef.current?.clear(); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {hasChildren && (
        <div className="thread-wrapper">
          <div
            className={`thread-line${collapsed && !forceExpandIds?.has(comment.id) ? ' thread-line--collapsed' : ''}`}
            onClick={() => setCollapsed(v => !v)}
            title={collapsed ? 'Expand replies' : 'Collapse replies'}
          />
          <div className="thread-children">
            {collapsed && !forceExpandIds?.has(comment.id) ? (
              <button className="thread-collapsed-btn" onClick={() => setCollapsed(false)}>
                {comment.children.length} {comment.children.length === 1 ? 'reply' : 'replies'} hidden — click to expand
              </button>
            ) : (
              <>
                {visibleChildren.map(child => (
                  <CommentNode
                    key={child.id}
                    comment={child}
                    announcementId={announcementId}
                    currentUser={currentUser}
                    isAdmin={isAdmin}
                    onDelete={onDelete}
                    onReply={onReply}
                    onProfileOpen={onProfileOpen}
                    depth={depth + 1}
                    forceExpandIds={forceExpandIds}
                    focusCommentId={focusCommentId}
                  />
                ))}
                {!effectiveThreadExpanded && hiddenChildCount > 0 && (
                  <button className="thread-show-more-btn" onClick={() => setThreadExpanded(true)}>
                    Show {hiddenChildCount} more {hiddenChildCount === 1 ? 'reply' : 'replies'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MIN_CLAMPED_TEXT_HEIGHT = 64;
const CONTENT_CHAR_LIMIT = 500;

const AnnouncementCard = ({
  announcement, currentUser, isAdmin, onCommentAdded, onAnnouncementDeleted,
  hideComments = false, clampTextBy = 0, isFocused = false, focusCommentId = null,
  autoOpenCommentForm = false, noCharLimit = false,
}) => {
  const navigate = useNavigate();
  const isAuthenticated = Boolean(currentUser && currentUser.id);
  const isPending = announcement.status === 'pending';
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentData, setCommentData] = useState({ text: '', mentions: [] });
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [expandedPdf, setExpandedPdf] = useState(null);
  const [openImgs, setOpenImgs] = useState(() => new Set()); // image attachments expanded
  const toggleImg = (id) => setOpenImgs(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [pdfBlobUrls, setPdfBlobUrls] = useState({});
  const [loadingComments, setLoadingComments] = useState(true);
  const [reactions, setReactions] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [poppingEmoji, setPoppingEmoji] = useState(null);
  const pickerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ title: announcement.title, content: announcement.content, post_date: toDateInputValue(announcement.created_at) });
  const [isPinned, setIsPinned] = useState(Boolean(announcement.pinned));
  const [profilePopupId, setProfilePopupId] = useState(null);
  const [poll, setPoll] = useState(null);
  const [pendingEditFiles, setPendingEditFiles] = useState([]);
  const [editFileKey, setEditFileKey] = useState(0);
  const editFileInputRef = useRef(null);
  const editRichRef      = useRef(null);
  const commentInputRef = useRef(null);
  const textRef = useRef(null);
  const [textClampHeight, setTextClampHeight] = useState(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || !clampTextBy) { setTextClampHeight(null); return; }
    const natural = el.scrollHeight;
    const target = Math.max(natural - clampTextBy, MIN_CLAMPED_TEXT_HEIGHT);
    setTextClampHeight(target < natural - 1 ? target : null);
  }, [clampTextBy, announcement.content]);

  const myReaction = currentUser
    ? (reactions.find(r => r.reactor_ids.includes(currentUser.id)) || null)
    : null;

  useEffect(() => {
    setIsPinned(Boolean(announcement.pinned));
  }, [announcement.pinned]);

  const fetchAnnouncementDetails = useCallback(async () => {
    try {
      const response = await getAnnouncementById(announcement.id);
      setComments(response.data.comments || []);
      setAttachments(response.data.attachments || []);
      setReactions(response.data.reactions || []);
      setIsPinned(Boolean(response.data.pinned));
      setLoadingComments(false);
    } catch (error) {
      console.error('Error loading announcement details:', error);
      setLoadingComments(false);
    }
    try {
      const pollRes = await getPoll('announcement', announcement.id, currentUser?.id);
      setPoll(pollRes.data);
    } catch {}
  }, [announcement.id, currentUser?.id]);

  useEffect(() => {
    fetchAnnouncementDetails();
    const handler = (id) => { if (id === announcement.id) fetchAnnouncementDetails(); };
    socket.on('announcement:changed', handler);
    return () => socket.off('announcement:changed', handler);
  }, [fetchAnnouncementDetails, announcement.id]);

  const goToAnnouncement = (commentId = null, openCommentFormFlag = false) => {
    navigate('/announcements', {
      state: { focusAnnouncementId: announcement.id, focusCommentId: commentId, openCommentForm: openCommentFormFlag },
    });
  };

  const forceExpandIds = useMemo(() => {
    if (!focusCommentId) return new Set();
    const parentOf = {};
    comments.forEach(c => { parentOf[c.id] = c.parent_id; });
    const ids = new Set();
    let cur = parentOf[focusCommentId];
    while (cur != null) {
      ids.add(cur);
      cur = parentOf[cur];
    }
    return ids;
  }, [comments, focusCommentId]);

  useEffect(() => {
    if (!isFocused) return;
    if (focusCommentId && loadingComments) return;
    const targetId = focusCommentId ? `comment-${focusCommentId}` : `announcement-${announcement.id}`;
    const t = setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, focusCommentId ? 150 : 50);
    return () => clearTimeout(t);
  }, [isFocused, focusCommentId, loadingComments, announcement.id]);

  useEffect(() => {
    if (isFocused && autoOpenCommentForm) setShowCommentForm(true);
  }, [isFocused, autoOpenCommentForm]);

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const handleReact = async (emoji) => {
    if (!isAuthenticated) {
      alert('Please sign in to react to announcements.');
      return;
    }
    try {
      if (myReaction?.emoji === emoji) {
        await unreactToAnnouncement(announcement.id, currentUser.id);
      } else {
        await reactToAnnouncement(announcement.id, currentUser.id, emoji);
        setPoppingEmoji(emoji);
        setTimeout(() => setPoppingEmoji(null), 400);
      }
      await fetchAnnouncementDetails();
    } catch (error) {
      console.error('Error reacting:', error);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      alert('Please sign in to post comments.');
      return;
    }
    if (!commentData.text.trim()) return;

    try {
      await commentOnAnnouncement(announcement.id, {
        author_id: currentUser.id,
        comment: commentData.text,
        mentions: commentData.mentions,
      });
      setCommentData({ text: '', mentions: [] });
      commentInputRef.current?.clear();
      setShowCommentForm(false);
      await fetchAnnouncementDetails();
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await updateAnnouncement(announcement.id, {
        title: editData.title,
        content: editData.content,
        post_date: editData.post_date || null,
        staff_id: currentUser.id,
      });
      if (pendingEditFiles.length > 0) {
        const fd = new FormData();
        pendingEditFiles.forEach(f => fd.append('files', f));
        await uploadAttachments(announcement.id, fd);
        setPendingEditFiles([]);
      }
      setIsEditing(false);
      setMenuOpen(false);
      await fetchAnnouncementDetails();
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      console.error('Error updating announcement:', error);
    }
  };

  const handlePinToggle = async (pinned) => {
    try {
      await pinAnnouncement(announcement.id, { pinned, staff_id: currentUser.id });
      setIsPinned(Boolean(pinned));
      setMenuOpen(false);
      await fetchAnnouncementDetails();
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      console.error('Error pinning announcement:', error);
    }
  };

  const handleDeleteAnnouncement = async () => {
    try {
      await deleteAnnouncement(announcement.id, { staff_id: currentUser.id });
      if (onAnnouncementDeleted) onAnnouncementDeleted();
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteAnnouncementComment(announcement.id, commentId, currentUser.id);
      await fetchAnnouncementDetails();
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await deleteAttachment(attachmentId, currentUser.id);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      if (expandedPdf === attachmentId) setExpandedPdf(null);
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const handleDownloadAttachment = async (attachmentId, originalName) => {
    try {
      const response = await fetch(`/api/attachments/${attachmentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('intranet_token')}`
        }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };

  const handlePreviewPdf = async (attachmentId) => {
    if (expandedPdf === attachmentId) {
      setExpandedPdf(null);
      return;
    }
    if (pdfBlobUrls[attachmentId]) {
      setExpandedPdf(attachmentId);
      return;
    }
    try {
      const response = await fetch(`/api/attachments/${attachmentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('intranet_token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to load PDF');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrls(prev => ({ ...prev, [attachmentId]: blobUrl }));
      setExpandedPdf(attachmentId);
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      alert('Failed to load PDF preview');
    }
  };

  const commentLabel = (() => {
    const name = announcement.featured_commenter_name;
    const count = Number(announcement.commenter_count ?? announcement.comment_count ?? 0);
    if (!name || count === 0) return 'No comments';
    const others = count - 1;
    if (others === 0) return `${name} commented`;
    return `${name} and ${others} ${others === 1 ? 'other' : 'others'} commented`;
  })();

  const contentIsHTML = isHTML(announcement.content);
  const isTruncated = !contentIsHTML && hideComments && !noCharLimit && (announcement.content || '').length > CONTENT_CHAR_LIMIT;
  const displayedContent = isTruncated
    ? announcement.content.slice(0, CONTENT_CHAR_LIMIT).trimEnd() + '…'
    : announcement.content;

  return (
    <div
      className={`announcement-card${isFocused && !focusCommentId ? ' announcement-card--focus' : ''}${isPending ? ' announcement-card--pending' : ''}`}
      id={`announcement-${announcement.id}`}
    >
      <div className="announcement-header">
        <div>
          <div className="announcement-title-row">
            <h3
              className={hideComments ? 'announcement-title--clickable' : ''}
              onClick={hideComments ? () => goToAnnouncement() : undefined}
            >
              {announcement.title}
            </h3>
            {isPending ? <span className="pending-badge">Pending Approval</span> : null}
            {isPinned ? <span className="pinned-badge">Pinned</span> : null}
          </div>
          <p className="announcement-date">{formatPostDate(announcement.created_at)}</p>
          <p className="announcement-author">Posted by <span className="author-link" onClick={() => setProfilePopupId(announcement.author_id)}>{announcement.author_name}</span></p>
        </div>
        {(isAdmin || (currentUser && currentUser.id === announcement.author_id)) && (
          <div className="action-menu-container">
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>⋯</button>
            {menuOpen && (
              <div className="action-dropdown">
                <button type="button" onClick={() => { setIsEditing(true); setMenuOpen(false); }}>Edit</button>
                <button type="button" onClick={handleDeleteAnnouncement}>Delete</button>
                {isAdmin && (
                  <button type="button" onClick={() => handlePinToggle(!announcement.pinned)}>
                    {announcement.pinned ? 'Unpin' : 'Pin'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="announcement-edit-form">
          <input
            type="text"
            value={editData.title}
            onChange={(e) => setEditData({ ...editData, title: e.target.value })}
            className="form-input"
          />
          <label className="announcement-postdate-field">
            <span className="announcement-postdate-label">Post date</span>
            <input
              type="date"
              value={editData.post_date}
              onChange={(e) => setEditData({ ...editData, post_date: e.target.value })}
              className="form-input announcement-postdate-input"
            />
          </label>
          <RichTextInput
            ref={editRichRef}
            showToolbar
            htmlMode
            className="rich-input--form"
            defaultHTML={
              isHTML(announcement.content)
                ? announcement.content
                : (announcement.content || '').replace(/\n/g, '<br>')
            }
            onChangeValue={(html) => setEditData(prev => ({ ...prev, content: html }))}
          />

          {attachments.length > 0 && (
            <div className="edit-attachments">
              {attachments.map(att => (
                <div key={att.id} className="edit-attachment-row">
                  <span className="attachment-icon">{att.mimetype === 'application/pdf' ? '📄' : att.mimetype.startsWith('image/') ? '🖼️' : '📎'}</span>
                  <span className="edit-attachment-name">{att.original_name}</span>
                  <button type="button" className="attachment-delete-btn" onClick={() => handleDeleteAttachment(att.id)} title="Remove">✕</button>
                </div>
              ))}
            </div>
          )}

          {pendingEditFiles.length > 0 && (
            <ul className="pending-files">
              {pendingEditFiles.map((f, i) => (
                <li key={i}>
                  <span>{f.name}</span>
                  <button type="button" onClick={() => setPendingEditFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
                </li>
              ))}
            </ul>
          )}

          <input key={editFileKey} id={`edit-file-input-${announcement.id}`} ref={editFileInputRef} type="file" multiple hidden
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.txt"
            onChange={e => { setPendingEditFiles(prev => [...prev, ...Array.from(e.target.files)]); setEditFileKey(k => k + 1); }} />

          <div className="edit-actions-row">
            <label htmlFor={`edit-file-input-${announcement.id}`} className="split-btn split-btn--sm" style={{ cursor: 'pointer' }}>
              <span className="split-btn-main">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                </svg>
                Attach files
              </span>
              <span className="split-btn-caret">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="2,3 5,7 8,3"/></svg>
              </span>
            </label>
            <div className="split-btn split-btn--sm split-btn--action" onClick={handleSaveEdit}>
              <span className="split-btn-main">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Save
              </span>
              <span className="split-btn-caret">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="2,3 5,7 8,3"/></svg>
              </span>
            </div>
            <button type="button" className="edit-cancel-btn" onClick={() => { setIsEditing(false); setPendingEditFiles([]); setEditData({ title: announcement.title, content: announcement.content, post_date: toDateInputValue(announcement.created_at) }); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div
          className="announcement-content"
          ref={textRef}
          style={(!noCharLimit && textClampHeight) ? { maxHeight: textClampHeight, overflow: 'hidden' } : undefined}
        >
          {contentIsHTML ? (
            <div
              className="announcement-html-body"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(displayedContent) }}
            />
          ) : (
            <p>{renderContent(displayedContent, announcement.mentions, setProfilePopupId)}</p>
          )}
        </div>
      )}
      {!noCharLimit && (textClampHeight || isTruncated) && (
        <button className="announcement-show-more" onClick={() => goToAnnouncement()}>
          Show more
        </button>
      )}

      {poll && (
        <PollBlock poll={poll} currentUser={currentUser} onVoted={fetchAnnouncementDetails} />
      )}

      {attachments.length > 0 && (
        <div className="attachments-section">
          {attachments.map(att => {
            const isPdf = att.mimetype === 'application/pdf';
            const isImage = att.mimetype.startsWith('image/');
            const url = `/api/attachments/${att.id}`;
            const canDelete = isAuthenticated && (isAdmin || (currentUser && currentUser.id === announcement.author_id));
            return (
              <div key={att.id} className="attachment-item">
                <div className="attachment-header">
                  <span className="attachment-icon">{isPdf ? '📄' : isImage ? '🖼️' : '📎'}</span>
                  <span className="attachment-name">{att.original_name}</span>
                  <div className="attachment-actions">
                    <button className="attachment-download" onClick={() => handleDownloadAttachment(att.id, att.original_name)} title="Download">↓</button>
                    {isPdf && (
                      <button
                        className="attachment-preview-btn"
                        onClick={() => handlePreviewPdf(att.id)}
                      >
                        {expandedPdf === att.id ? 'Hide' : 'Preview'}
                      </button>
                    )}
                    {isImage && (
                      <button className="attachment-preview-btn" onClick={() => toggleImg(att.id)}>
                        {openImgs.has(att.id) ? 'Hide' : 'View'}
                      </button>
                    )}
                    {canDelete && (
                      <button className="attachment-delete-btn" onClick={() => handleDeleteAttachment(att.id)} title="Delete">✕</button>
                    )}
                  </div>
                </div>
                {isPdf && expandedPdf === att.id && pdfBlobUrls[att.id] && (
                  <iframe className="pdf-viewer" src={pdfBlobUrls[att.id]} title={att.original_name} />
                )}
                {isImage && openImgs.has(att.id) && (
                  <img src={url} alt={att.original_name} className="attachment-image" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {isPending ? (
        <div className="pending-footer-note">🔒 Only visible to you — awaiting admin approval</div>
      ) : (
      <>
      <div className="announcement-footer">
        <div className="reactions-row" ref={pickerRef}>
          {reactions.map(r => {
            const isMine = currentUser && r.reactor_ids.includes(currentUser.id);
            return (
              <ReactionPill
                key={r.emoji}
                r={r}
                isMine={isMine}
                isPopping={poppingEmoji === r.emoji}
                onClick={() => handleReact(r.emoji)}
              />
            );
          })}
          {isAuthenticated && (
            <button className="add-reaction-btn" onClick={() => setPickerOpen(v => !v)} title="Add reaction">
              <span>😊</span><span className="add-reaction-plus">+</span>
            </button>
          )}
          {pickerOpen && (
            <div className="emoji-picker-wrapper">
              <div className="emoji-picker-quick">
                {REACTION_EMOJIS.map(e => (
                  <button key={e} className="emoji-picker-quick-btn" onClick={() => { handleReact(e); setPickerOpen(false); }}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          className="comment-btn"
          onClick={() => hideComments ? goToAnnouncement(null, true) : setShowCommentForm(!showCommentForm)}
        >
          <span className="icon">💬</span>
          {commentLabel}
        </button>
      </div>

      {!hideComments && comments.length > 0 && (
        <div className="comment-list">
          <h4>Comments</h4>
          {loadingComments ? (
            <p>Loading comments...</p>
          ) : (() => {
            const roots = buildTree(comments);
            return (
              <div className="comment-list-inner">
                {roots.map(root => (
                  <CommentNode
                    key={root.id}
                    comment={root}
                    announcementId={announcement.id}
                    currentUser={currentUser}
                    isAdmin={isAdmin}
                    onDelete={handleDeleteComment}
                    onReply={fetchAnnouncementDetails}
                    onProfileOpen={setProfilePopupId}
                    forceExpandIds={forceExpandIds}
                    focusCommentId={focusCommentId}
                  />
                ))}
              </div>
            );
          })()}
        </div>
      )}
      {!hideComments && showCommentForm && isAuthenticated && (
        <form onSubmit={handleComment} className="comment-form">
          <RichTextInput
            ref={commentInputRef}
            placeholder="Add a comment..."
            className="rich-input--comment"
            onChangeValue={(text, mentions) => setCommentData({ text, mentions })}
          />
          <button type="submit">Post Comment</button>
        </form>
      )}
      </>
      )}
      {profilePopupId && (
        <StaffProfilePopup staffId={profilePopupId} onClose={() => setProfilePopupId(null)} />
      )}
    </div>
  );
};

export default AnnouncementCard;
